import { useLayoutEffect, useRef, useSyncExternalStore, type KeyboardEvent, type MouseEvent, type ReactElement } from "react";
import type { Rules } from "odyssey-shared";
import { Button, EmptyState, ITEM_SLOT_ART_BOX, ItemSlot, PlayWindow, logical, type PlaceSource, type SizeSource } from "../../ui/kit/index.ts";
import { itemGlyph, slotLabel } from "../bag/describe.ts";
import { itemArt, useIcons } from "../icons/context.tsx";
import type { HudArea } from "../hud/slots.ts";
import type { WindowOrder } from "../windows/order.ts";
import { windowPlace, windowSize } from "../windows/places.ts";
import { useRaise } from "../windows/raise.ts";
import { itemName, takeLabel } from "./describe.ts";
import { CHEST_WINDOW, chestRoom, chestWindowBox } from "./layout.ts";
import { askable, shownOf, untaken, type Chest, type ChestLine } from "./lines.ts";
import type { ChestSource } from "./store.ts";
import { say } from "../../words.ts";

/**
 * The loot window (P2.8-T16, ADR-091 decisions 19 and 22; the design's `loot` card in `ItemSurfaces.jsx`, a visual
 * reference): the kit's `Window` titled "Chest", 150 wide beside the character, a row for each line the character
 * has not taken (a 20 px item slot, the item's name, a Take button) and Take all in its footer. It draws only what the
 * chest store holds, so a row leaves when the server says its line was taken and never before.
 *
 * - A row whose take waits for the server shows the slot's hatch and its Take is disabled; while any take waits, Take
 *   all shows its busy dots under the name "Taking". A row the server refused for want of room is drawn disabled until
 *   the bag changes, its Take's accessible name saying why; the sentence itself stands on the HUD's message line.
 * - With no line at all it shows "Nothing left to take.", and once every line is taken "You have already looted
 *   this.", each the design's empty state in the rows' place with no footer.
 * - It is a play window (ADR-061's amendments "a surface that is not modal never takes the game's keys", "a play
 *   window never stops play" and "a window can be moved"): its layer and its keys are `keys.ts`'s. It opens taking no
 *   focus and a pointer press never leaves focus on one of its controls, so the keyboard stays the game's; asked to be
 *   entered (Shift+E) it focuses its first Take, so Tab then walks its controls. A take made from the keyboard keeps
 *   the focus in the window as its row is disabled or leaves: a browser leaves focus on a button that was disabled
 *   under it, where no key reaches it, so the focus moves on to the next control a take can be made from. As the window closes, focus still inside it goes back
 *   to what had it, or leaves for the page.
 * - A held key takes once: a repeat of Enter or Space on a control of the window is not a press.
 * - It can be moved and resized, as every game window can (P1.9-T183, ADR-061's amendment "every game window moves and
 *   resizes"): its width is free between 150 and 300 and its height steps by a row (26), so a kept size shows a whole
 *   number of rows. A window never resized is as tall as its rows need; once resized it keeps its height whatever
 *   the rows left, and the rows that do not fit scroll.
 * - Its content scrolls down inside a window shortened by a small area or by the viewer, never across.
 */

/**
 * Where the keyboard's focus goes as the window is entered, the first that the window holds: a row's Take that can be
 * pressed, then Take all, then the close button.
 */
const ENTRY: readonly string[] = ["[data-chest='row'] [data-kit='button']:not(:disabled)", "[data-kit='window-footer'] [data-kit='button']:not(:disabled)", "[data-kit='window-header'] button"];

/**
 * Focuses the window's entry control.
 *
 * @param box The window's box.
 */
function focusEntry(box: HTMLElement): void {
  for (const selector of ENTRY) {
    const control = box.querySelector<HTMLElement>(selector);
    if (control !== null) {
      control.focus();
      return;
    }
  }
}

/**
 * Keeps a pointer press on the window's rows and footer from taking focus, so a control left focused never answers
 * Space or Enter meant for the game.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * Refuses a held key's repeats on a control: one press of Enter or Space is one take.
 *
 * @param event The key press.
 */
function oncePerPress(event: KeyboardEvent<HTMLElement>): void {
  if (event.repeat && (event.key === "Enter" || event.key === " ")) {
    event.preventDefault();
  }
}

/**
 * One row: the line's item in a slot, its name, and its Take.
 *
 * @param props The `line`, the `rules` its item is named and drawn by, and `onTake`, called with whether the press
 *   came from the keyboard.
 * @returns The row.
 */
function LootRow({ line, rules, onTake }: { readonly line: ChestLine; readonly rules: Rules; readonly onTake: (keyboard: boolean) => void }): ReactElement {
  const blocked = line.full || line.pending;
  const icons = useIcons();
  return (
    <li className={`ot-chest__row${line.full ? " is-full" : ""}`} data-chest="row" data-line={line.line} data-full={line.full ? "true" : undefined} data-pending={line.pending ? "true" : undefined} style={{ height: logical(CHEST_WINDOW.slot), columnGap: logical(CHEST_WINDOW.nameGap) }}>
      <ItemSlot label={slotLabel(rules, line.item, line.quantity)} rarity={rules.items.get(line.item)?.rarity ?? null} art={itemArt(icons, rules, line.item, ITEM_SLOT_ART_BOX)} glyph={itemGlyph(rules, line.item)} count={line.quantity} size={CHEST_WINDOW.slot} pending={line.pending} disabled={line.full} tabStop={false} onClick={() => (blocked ? undefined : onTake(false))} />
      <span className="ot-chest__name" data-chest="name">
        {itemName(rules, line.item)}
      </span>
      <Button label={takeLabel(rules, line.item, line.quantity, line.full)} disabled={blocked} onClick={(event) => onTake(event.detail === 0)}>
        {say("chest.take")}
      </Button>
    </li>
  );
}

/**
 * The window while open: its box, its focus and its content.
 *
 * @param props The `area` it is laid out on, the `chest` shown and what the HUD hands the window (`hud`).
 * @returns The window in its place.
 */
function OpenChest({ area, chest, hud }: { readonly area: HudArea; readonly chest: Chest; readonly hud: HudChest }): ReactElement {
  const host = useRef<HTMLDivElement>(null);
  const keyboard = useRef(false);
  const front = useRaise(host, hud.order, "chest");
  const rows = untaken(chest);
  const shown = shownOf(chest);
  const waiting = rows.some((each) => each.pending);

  useLayoutEffect(() => {
    const box = host.current;
    const opener = document.activeElement;
    return () => {
      if (box !== null && box.contains(document.activeElement)) {
        if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected && !box.contains(opener)) {
          opener.focus();
        } else {
          (document.activeElement as HTMLElement).blur();
        }
      }
    };
  }, []);

  useLayoutEffect(() => {
    const box = host.current;
    if (box === null) {
      return undefined;
    }
    /**
     * Gives the keyboard up when the player presses or focuses anything outside the window.
     *
     * @param event The press or the focus.
     */
    const elsewhere = (event: Event): void => {
      if (!(event.target instanceof Node) || !box.contains(event.target)) {
        keyboard.current = false;
      }
    };
    document.addEventListener("pointerdown", elsewhere, true);
    document.addEventListener("focusin", elsewhere, true);
    return () => {
      document.removeEventListener("pointerdown", elsewhere, true);
      document.removeEventListener("focusin", elsewhere, true);
    };
  }, []);

  useLayoutEffect(() => {
    const box = host.current;
    if (box === null) {
      return;
    }
    const entered = hud.takeEntry?.() === true;
    if (entered) {
      keyboard.current = true;
    }
    const active = document.activeElement;
    const lost = !box.contains(active) || (active instanceof HTMLButtonElement && active.disabled);
    if (entered || (keyboard.current && lost)) {
      focusEntry(box);
    }
  }, [chest, hud]);

  /**
   * Notes whether a take came from the keyboard, whose focus the window then keeps, and makes it.
   *
   * @param fromKeyboard Whether the press was a key's.
   * @param take The take.
   */
  const taking = (fromKeyboard: boolean, take: () => void): void => {
    keyboard.current = fromKeyboard;
    take();
  };

  return (
    <PlayWindow
      id="chest"
      title={say("chest.title")}
      onClose={hud.onClose}
      box={chestWindowBox(area, rows.length)}
      room={chestRoom(area)}
      place={hud.place ?? windowPlace("chest")}
      size={hud.size ?? windowSize("chest")}
      host={host}
      className={front ? "ot-chest is-front" : "ot-chest"}
      data={{ "data-chest": "window", "data-chest-state": shown, "data-chest-handle": String(chest.handle) }}
      footer={
        shown !== "available" ? undefined : (
          <div className="ot-chest__footer" onMouseDown={keepFocus} onKeyDown={oncePerPress}>
            <Button variant="primary" fullWidth busy={waiting} busyLabel={say("chest.taking")} disabled={!waiting && askable(chest).length === 0} onClick={(event) => taking(event.detail === 0, hud.onTakeAll)}>
              {say("chest.takeAll")}
            </Button>
          </div>
        )
      }
    >
      {shown === "available" ? (
        <ul className="ot-chest__rows" data-chest="rows" aria-label={say("chest.rows")} onMouseDown={keepFocus} onKeyDown={oncePerPress} style={{ rowGap: logical(CHEST_WINDOW.rowGap) }}>
          {rows.map((line) => (
            <LootRow key={line.line} line={line} rules={hud.rules} onTake={(fromKeyboard) => taking(fromKeyboard, () => hud.onTake(line.line))} />
          ))}
        </ul>
      ) : (
        <div className="ot-chest__empty" data-chest="empty" style={{ height: logical(CHEST_WINDOW.emptyHeight) }}>
          <EmptyState glyph="briefcase">{say(`chest.${shown}`)}</EmptyState>
        </div>
      )}
    </PlayWindow>
  );
}

/**
 * What the HUD needs to draw the window: the chest's `source`; the `rules` its items are named and drawn by;
 * `takeEntry`, when the page has keys, which answers once whether the window was asked to take the focus; `onTake`,
 * called with the line a Take chose; `onTakeAll`; `onClose`, which the close button calls; where the window's place and
 * its size are kept (`place`, `size`), the page's own for the loot window unless given; and the play windows' `order`, on a page with more
 * than one.
 */
export interface HudChest {
  readonly source: ChestSource;
  readonly rules: Rules;
  readonly takeEntry?: (() => boolean) | undefined;
  readonly onTake: (line: number) => void;
  readonly onTakeAll: () => void;
  readonly onClose: () => void;
  readonly place?: PlaceSource | undefined;
  readonly size?: SizeSource | undefined;
  readonly order?: WindowOrder | undefined;
}

/**
 * The loot window, shown while the chest store holds a chest.
 *
 * @param props The `area` it is laid out on and what the HUD hands it (`chest`).
 * @returns The window, or nothing while closed.
 * @example
 * <ChestWindow area={layout} chest={{ source: chest, rules: PAGE_RULES, onTake: (line) => void chest.take(line), onTakeAll: () => void chest.takeAll(), onClose: () => chest.close() }} />
 */
export function ChestWindow({ area, chest }: { readonly area: HudArea; readonly chest: HudChest }): ReactElement | null {
  const snapshot = useSyncExternalStore(chest.source.subscribe, chest.source.getSnapshot, chest.source.getSnapshot);
  return snapshot === null ? null : <OpenChest area={area} chest={snapshot} hud={chest} />;
}
